import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

import { db } from "../firebase/config";

const categoriasPadrao = [
  { nome: "Vendas", tipo: "receita", icone: "💰" },
  { nome: "Serviços", tipo: "receita", icone: "🧾" },
  { nome: "Fornecedor", tipo: "despesa", icone: "📦" },
  { nome: "Aluguel", tipo: "despesa", icone: "🏠" },
  { nome: "Transporte", tipo: "despesa", icone: "🚚" },
  { nome: "Alimentação", tipo: "despesa", icone: "🍽️" },
  { nome: "Outros", tipo: "despesa", icone: "⚙️" },
];

function inicioTrial() {
  return Timestamp.now();
}

function fimTrial() {
  const data = new Date();
  data.setDate(data.getDate() + 15);
  return Timestamp.fromDate(data);
}

function gerarMesChave(data) {
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}`;
}

function periodoParaMesChave(periodo) {
  if (!periodo) return gerarMesChave(new Date());

  if (/^\d{4}-\d{2}$/.test(periodo)) return periodo;

  const meses = {
    janeiro: "01",
    fevereiro: "02",
    marco: "03",
    março: "03",
    abril: "04",
    maio: "05",
    junho: "06",
    julho: "07",
    agosto: "08",
    setembro: "09",
    outubro: "10",
    novembro: "11",
    dezembro: "12",
  };

  const texto = String(periodo).toLowerCase().trim();
  const partes = texto.split(/\s+/);
  const mes = meses[partes[0]];
  const ano = partes.find((parte) => /^\d{4}$/.test(parte));

  if (mes && ano) return `${ano}-${mes}`;

  return gerarMesChave(new Date());
}

function validarUid(uid) {
  if (!uid) {
    throw new Error("Usuário não encontrado. Faça login novamente.");
  }
}

export async function prepararUsuarioNovo(uid, nomeNegocio, email) {
  validarUid(uid);

  const userRef = doc(db, "usuarios", uid);
  const userSnap = await getDoc(userRef);

  const configRef = doc(db, "usuarios", uid, "config", "app");
  const configSnap = await getDoc(configRef);

  const billingRef = doc(db, "usuarios", uid, "private", "billing");
  const billingSnap = await getDoc(billingRef);

  const categoriasRef = collection(db, "usuarios", uid, "categorias");

  const batch = writeBatch(db);

  if (!userSnap.exists()) {
    batch.set(userRef, {
      email: email || "",
      nome: nomeNegocio || "",
      criadoEm: serverTimestamp(),
      atualizadoEm: serverTimestamp(),
      app: "invexa-finance",
    });
  } else {
    batch.set(
      userRef,
      {
        email: email || userSnap.data()?.email || "",
        nome: nomeNegocio || userSnap.data()?.nome || "",
        atualizadoEm: serverTimestamp(),
        app: "invexa-finance",
      },
      { merge: true }
    );
  }

  if (!configSnap.exists()) {
    batch.set(configRef, {
      nomeNegocio: nomeNegocio || "Meu negócio",
      moeda: "BRL",
      criadoEm: serverTimestamp(),
      atualizadoEm: serverTimestamp(),
    });
  }

  if (!billingSnap.exists()) {
    batch.set(billingRef, {
      status: "trialing",
      trialStartedAt: inicioTrial(),
      trialEndsAt: fimTrial(),
      accessExpiresAt: fimTrial(),
      assinaturaAtiva: false,
      paymentApproved: false,
      plano: "free",
      app: "invexa-finance",
      criadoEm: serverTimestamp(),
      atualizadoEm: serverTimestamp(),
    });
  }

  if (!userSnap.exists()) {
    categoriasPadrao.forEach((categoria) => {
      const catRef = doc(categoriasRef);
      batch.set(catRef, {
        ...categoria,
        criadoEm: serverTimestamp(),
      });
    });
  }

  await batch.commit();
}

export function ouvirConfiguracao(uid, callback) {
  if (!uid) return () => {};

  return onSnapshot(
    doc(db, "usuarios", uid, "config", "app"),
    (snap) => callback(snap.exists() ? snap.data() : null),
    (error) => {
      console.error("Erro ao ouvir configuração:", error);
      callback(null);
    }
  );
}

export function ouvirBilling(uid, callback) {
  if (!uid) return () => {};

  return onSnapshot(
    doc(db, "usuarios", uid, "private", "billing"),
    (snap) => callback(snap.exists() ? snap.data() : null),
    (error) => {
      console.error("Erro ao ouvir billing:", error);
      callback(null);
    }
  );
}

export function ouvirCategorias(uid, callback) {
  if (!uid) return () => {};

  const q = query(collection(db, "usuarios", uid, "categorias"), orderBy("nome", "asc"));

  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (error) => {
      console.error("Erro ao ouvir categorias:", error);
      callback([]);
    }
  );
}

export function ouvirMovimentacoesMes(uid, mesChave, callback) {
  if (!uid || !mesChave) return () => {};

  const q = query(
    collection(db, "usuarios", uid, "movimentacoes"),
    where("mesChave", "==", mesChave)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const lista = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const dataA = a.data?.toDate ? a.data.toDate().getTime() : 0;
          const dataB = b.data?.toDate ? b.data.toDate().getTime() : 0;
          return dataB - dataA;
        });

      callback(lista);
    },
    (error) => {
      console.error("Erro ao ouvir movimentações:", error);
      callback([]);
    }
  );
}

export async function salvarMovimentacao(uid, dados) {
  validarUid(uid);

  if (!dados?.tipo) throw new Error("Informe o tipo da movimentação.");
  if (!dados?.valor || Number(dados.valor) <= 0) throw new Error("Informe um valor válido.");

  const data = dados.data ? new Date(`${dados.data}T12:00:00`) : new Date();
  const mesChave = gerarMesChave(data);

  await addDoc(collection(db, "usuarios", uid, "movimentacoes"), {
    tipo: dados.tipo,
    descricao: dados.descricao || "Sem descrição",
    categoria: dados.categoria || "Outros",
    valor: Number(dados.valor),
    data: Timestamp.fromDate(data),
    dataISO: dados.data || new Date().toISOString().slice(0, 10),
    mesChave,
    observacao: dados.observacao || "",
    criadoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });
}

export async function atualizarMovimentacao(uid, movId, dados) {
  validarUid(uid);

  if (!movId) throw new Error("Movimentação não encontrada.");

  const data = dados.data ? new Date(`${dados.data}T12:00:00`) : new Date();
  const mesChave = gerarMesChave(data);

  await updateDoc(doc(db, "usuarios", uid, "movimentacoes", movId), {
    ...dados,
    valor: Number(dados.valor || 0),
    data: Timestamp.fromDate(data),
    dataISO: dados.data || new Date().toISOString().slice(0, 10),
    mesChave,
    atualizadoEm: serverTimestamp(),
  });
}

export async function excluirMovimentacao(uid, movId) {
  validarUid(uid);

  if (!movId) throw new Error("Movimentação não encontrada.");

  await deleteDoc(doc(db, "usuarios", uid, "movimentacoes", movId));
}

export async function salvarCategoria(uid, categoria) {
  validarUid(uid);

  if (!categoria?.nome) throw new Error("Informe o nome da categoria.");

  await addDoc(collection(db, "usuarios", uid, "categorias"), {
    nome: categoria.nome,
    tipo: categoria.tipo || "despesa",
    icone: categoria.icone || "🏷️",
    criadoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });
}

export async function excluirCategoria(uid, catId) {
  validarUid(uid);

  if (!catId) throw new Error("Categoria não encontrada.");

  await deleteDoc(doc(db, "usuarios", uid, "categorias", catId));
}

export function ouvirCofrinhos(uid, callback) {
  if (!uid) return () => {};

  return onSnapshot(
    collection(db, "usuarios", uid, "cofrinhos"),
    (snapshot) => {
      const lista = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const dataA = a.criadoEm?.toDate ? a.criadoEm.toDate().getTime() : 0;
          const dataB = b.criadoEm?.toDate ? b.criadoEm.toDate().getTime() : 0;
          return dataB - dataA;
        });

      callback(lista);
    },
    (error) => {
      console.error("Erro ao ouvir cofrinhos:", error);
      callback([]);
    }
  );
}

export async function salvarCofrinho(uid, dados) {
  validarUid(uid);

  if (!dados?.nome) throw new Error("Informe o nome do cofrinho.");
  if (!dados?.meta || Number(dados.meta) <= 0) throw new Error("Informe uma meta válida.");

  await addDoc(collection(db, "usuarios", uid, "cofrinhos"), {
    nome: dados.nome,
    icone: dados.icone || "🎯",
    meta: Number(dados.meta),
    valorAtual: Number(dados.valorAtual || 0),
    prazo: dados.prazo || "",
    observacao: dados.observacao || "",
    criadoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });
}

export async function excluirCofrinho(uid, cofrinhoId) {
  validarUid(uid);

  if (!cofrinhoId) throw new Error("Cofrinho não encontrado.");

  await deleteDoc(doc(db, "usuarios", uid, "cofrinhos", cofrinhoId));
}

export async function movimentarCofrinho(uid, cofrinhoId, dados) {
  validarUid(uid);

  if (!cofrinhoId) throw new Error("Cofrinho não encontrado.");

  const valor = Number(dados.valor || 0);

  if (!valor || valor <= 0) throw new Error("Informe um valor válido.");

  const tipo = dados.tipo === "retirar" ? "retirar" : "guardar";
  const ajuste = tipo === "retirar" ? -valor : valor;

  await updateDoc(doc(db, "usuarios", uid, "cofrinhos", cofrinhoId), {
    valorAtual: increment(ajuste),
    atualizadoEm: serverTimestamp(),
  });

  await addDoc(collection(db, "usuarios", uid, "cofrinhos", cofrinhoId, "historico"), {
    tipo,
    valor,
    observacao: dados.observacao || "",
    criadoEm: serverTimestamp(),
  });
}

export async function getResumoPeriodo(uid, periodo) {
  validarUid(uid);

  const mesChave = periodoParaMesChave(periodo);
  const q = query(
    collection(db, "usuarios", uid, "movimentacoes"),
    where("mesChave", "==", mesChave)
  );

  const snapshot = await getDocs(q);

  const resumo = snapshot.docs.reduce(
    (acc, item) => {
      const dados = item.data();
      const valor = Number(dados.valor || 0);

      if (dados.tipo === "receita") acc.entradas += valor;
      if (dados.tipo === "despesa") acc.saidas += valor;

      return acc;
    },
    { entradas: 0, saidas: 0, saldo: 0 }
  );

  resumo.saldo = resumo.entradas - resumo.saidas;
  return resumo;
}

export async function getUltimasTransacoes(uid, periodo) {
  validarUid(uid);

  const mesChave = periodoParaMesChave(periodo);
  const q = query(
    collection(db, "usuarios", uid, "movimentacoes"),
    where("mesChave", "==", mesChave)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .sort((a, b) => {
      const dataA = a.data?.toDate ? a.data.toDate().getTime() : 0;
      const dataB = b.data?.toDate ? b.data.toDate().getTime() : 0;
      return dataB - dataA;
    });
}

export async function getTopCategorias(uid, periodo) {
  validarUid(uid);

  const transacoes = await getUltimasTransacoes(uid, periodo);
  const mapa = new Map();

  transacoes.forEach((item) => {
    const nome = item.categoria || "Outros";
    const atual = mapa.get(nome) || 0;
    mapa.set(nome, atual + Number(item.valor || 0));
  });

  return Array.from(mapa.entries())
    .map(([nome, valor]) => ({ nome, valor }))
    .sort((a, b) => b.valor - a.valor);
}

export async function getInvestimentosPeriodo(uid) {
  validarUid(uid);

  const snapshot = await getDocs(collection(db, "usuarios", uid, "investimentos"));
  const total = snapshot.docs.reduce((acc, item) => acc + Number(item.data().valor || 0), 0);

  return { total };
}

export function ouvirInvestimentos(uid, callback) {
  if (!uid) return () => {};

  return onSnapshot(
    collection(db, "usuarios", uid, "investimentos"),
    (snapshot) => {
      callback(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    },
    (error) => {
      console.error("Erro ao ouvir investimentos:", error);
      callback([]);
    }
  );
}

export async function salvarInvestimento(uid, dados) {
  validarUid(uid);

  const ticker = String(dados?.ticker || dados?.nome || "").trim().toUpperCase();

  if (!ticker) throw new Error("Informe o ticker do investimento.");

  const quantidade = Number(dados.quantidade || 0);
  const precoMedio = Number(dados.precoMedio || 0);
  const valorInvestido = Number(dados.valorInvestido || dados.valor || quantidade * precoMedio || 0);

  await addDoc(collection(db, "usuarios", uid, "investimentos"), {
    ticker,
    nome: dados.nome || ticker,
    tipo: dados.tipo || "Ações",
    quantidade,
    precoMedio,
    valorInvestido,
    valor: valorInvestido,
    rentabilidade: 0,
    criadoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });
}

export async function excluirInvestimento(uid, investimentoId) {
  validarUid(uid);

  if (!investimentoId) throw new Error("Investimento não encontrado.");

  await deleteDoc(doc(db, "usuarios", uid, "investimentos", investimentoId));
}
